import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { compositionForGame, DEFAULT_PLAYER_NAMES, DEFAULT_SETTINGS, presetForCount, ROLE_PRESETS, type GameSettings, type TeamSummary } from '@shum/shared';
import { api } from '../lib/api';
import { GameLayout } from './GameLayout';

const LAST_MAFIA_SETTINGS_KEY = 'shum-mafia-last-settings';

export function CreateRoom() {
  const navigate = useNavigate();
  const location = useLocation();
  const [names, setNames] = useState<string[]>([...DEFAULT_PLAYER_NAMES]);
  const [hostIndex, setHostIndex] = useState(0);
  const [draft, setDraft] = useState('');
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [teamName, setTeamName] = useState('');
  const [settings, setSettings] = useState<Partial<GameSettings>>(() => initialSettings(location.state));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.listTeams().then(setTeams).catch(() => {});
  }, []);

  const count = names.filter((n) => n.trim()).length;
  const hostName = names[hostIndex]?.trim() ?? '';
  const preset = presetForCount(count);
  const validation = countValidation(count);

  const selectedPreset = ROLE_PRESETS.find((p) => p.id === settings.presetId && count >= p.minPlayers && count <= p.maxPlayers);
  const effectivePreset = selectedPreset ?? preset;
  const effectiveSettings: GameSettings = { ...DEFAULT_SETTINGS, ...settings, presetId: effectivePreset.id };
  const comp = compositionForGame(effectivePreset, effectiveSettings);

  useEffect(() => {
    saveLastSettings({ ...settings, presetId: effectivePreset.id });
  }, [settings, effectivePreset.id]);

  function addPlayer() {
    const n = draft.trim();
    if (!n) return;
    if (names.some((x) => x.toLowerCase() === n.toLowerCase())) {
      setError('Такое имя уже есть.');
      return;
    }
    setNames((p) => [...p, n]);
    setDraft('');
    setError(null);
  }

  function removeAt(i: number) {
    setNames((p) => p.filter((_, idx) => idx !== i));
    setHostIndex((current) => {
      if (i < current) return current - 1;
      if (i === current) return Math.max(0, Math.min(current, names.length - 2));
      return current;
    });
  }

  function renameAt(i: number, value: string) {
    setNames((p) => p.map((x, idx) => (idx === i ? value : x)));
  }

  function loadTeam(id: string) {
    if (!id) return;
    api
      .getTeam(id)
      .then((t) => {
        setNames(t.players.map((p) => p.name));
        setHostIndex(0);
        setTeamName(t.name);
        setMsg(`Загружена команда «${t.name}»`);
      })
      .catch(() => setError('Не удалось загрузить команду.'));
  }

  async function saveTeam() {
    if (!teamName.trim()) {
      setError('Введите название команды.');
      return;
    }
    try {
      await api.createTeam(teamName.trim(), names);
      setTeams(await api.listTeams());
      setMsg('Команда сохранена.');
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function create() {
    if (!validation.ok) {
      setError(validation.message);
      return;
    }
    if (!hostName) {
      setError('Отметьте игрока, который будет ведущим.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.createRoom({
        players: names.filter((n) => n.trim()),
        creatorName: hostName,
        settings: { ...settings, presetId: effectivePreset.id },
      });
      saveLastSettings({ ...settings, presetId: effectivePreset.id });
      navigate(`/room/${res.code}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <GameLayout>
      <CreateStyles />
      <span className="label">/ Mafia Live · создание комнаты</span>
      <h1 className="cr-title">Кто сегодня играет?</h1>
      <p className="lede">
        Введите имена всех игроков заранее — от 5 до 12. Никаких прозвищ в процессе: каждый выберет своё имя из списка.
      </p>

      <div className="cr-layout">
        <div className="cr-main">
          <div className="panel">
            <div className="panel-head">
              <h2>Состав</h2>
              <span className="pill">{count} игроков</span>
            </div>

            <div className="team-row">
              <input
                className="text-input"
                placeholder="Название команды (необязательно)"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
              />
            </div>

            {teams.length > 0 && (
              <div className="team-row">
                <select className="text-input" value="" onChange={(e) => loadTeam(e.target.value)}>
                  <option value="">Загрузить сохранённую команду…</option>
                  {teams.map((t) => (
                    <option key={t.teamId} value={t.teamId}>
                      {t.name} · {t.playerCount} игр.
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="players">
              <p className="host-note">Отметьте одного игрока как ведущего — он будет запускать игру и переключать фазы.</p>
              {names.map((n, i) => (
                <div className="player-row" key={i}>
                  <span className="pidx mono">{(i + 1).toString().padStart(2, '0')}</span>
                  <input
                    className="text-input"
                    value={n}
                    onChange={(e) => renameAt(i, e.target.value)}
                    placeholder={`Игрок ${i + 1}`}
                  />
                  <label className="host-check" title="Назначить ведущим">
                    <input type="checkbox" checked={hostIndex === i} onChange={() => setHostIndex(i)} />
                    <span>Ведущий</span>
                  </label>
                  <button className="icon-btn" onClick={() => removeAt(i)} aria-label="Удалить" disabled={names.length <= 1}>
                    ×
                  </button>
                </div>
              ))}
            </div>

            <form
              className="add-row"
              onSubmit={(e) => {
                e.preventDefault();
                addPlayer();
              }}
            >
              <input
                className="text-input"
                placeholder="Имя нового игрока"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button type="submit" className="btn-ghost" disabled={names.length >= 12}>
                Добавить
              </button>
            </form>

            <div className="cr-actions">
              <button className="btn-ghost" onClick={saveTeam} disabled={!count}>
                Сохранить команду
              </button>
              <button className="btn-primary" onClick={create} disabled={!validation.ok || !hostName || busy}>
                {busy ? 'Создаём…' : 'Создать комнату →'}
              </button>
            </div>
            {!validation.ok && <div className="warn">{validation.message}</div>}
            {!hostName && <div className="warn">Выберите ведущего из заполненных имён.</div>}
            {error && <div className="warn warn-err">{error}</div>}
            {msg && <div className="warn warn-ok">{msg}</div>}
          </div>
        </div>

        <aside className="cr-side">
          <div className="panel">
            <div className="panel-head">
              <h2>Состав ролей</h2>
              <span className="label">авто · {preset.label}</span>
            </div>
            <div className="role-comp">
              <RoleLine label="Дом мафии" count={comp.mafia_don} accent />
              <RoleLine label="Мафия" count={comp.mafia} accent />
              <RoleLine label="Доктор" count={comp.doctor} />
              <RoleLine label="Комиссар" count={comp.detective} />
              <RoleLine label="Путана" count={comp.prostitute} />
              <RoleLine label="Мирные" count={comp.civilian} />
            </div>
            <div className="rule" />
            <p className="hint">
              Набор ролей подбирается под число игроков. Дом мафии — отдельная роль внутри мафии: ночью он вычисляет комиссара.
            </p>
            <select
              className="text-input"
              value={effectivePreset.id}
              onChange={(e) => setSettings((s) => ({ ...s, presetId: e.target.value }))}
            >
              {ROLE_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <div className="rule" />
            <label className="check">
              <input
                type="checkbox"
                checked={settings.includeDoctor ?? true}
                onChange={(e) => setSettings((s) => ({ ...s, includeDoctor: e.target.checked }))}
              />
              Играть с доктором
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={settings.includeProstitute ?? false}
                onChange={(e) => setSettings((s) => ({ ...s, includeProstitute: e.target.checked }))}
              />
              Играть с путаной
            </label>
            {settings.includeProstitute && (
              <p className="hint">
                Путана играет за мафию и с самого начала знает всю мафию.
              </p>
            )}
          </div>

          <div className="panel">
            <div className="panel-head">
              <h2>Тайминги и режим</h2>
            </div>
            <p className="hint">
              Ведущий жмёт «Дальше» только на этапе роли и голосования. Ночь идёт сама на таймерах и озвучке.
            </p>
            <label className="check">
              <input
                type="checkbox"
                checked={settings.startWithDay ?? false}
                onChange={(e) => setSettings((s) => ({ ...s, startWithDay: e.target.checked }))}
              />
              Альтернативное начало — сразу день (обсуждение и голосование)
            </label>
            {settings.startWithDay && (
              <p className="hint">
                Первая фаза после раздачи ролей — день, а не ночь. Дальше игра идёт как обычно.
              </p>
            )}
            <label className="check">
              <input
                type="checkbox"
                checked={settings.autoNight ?? true}
                onChange={(e) => setSettings((s) => ({ ...s, autoNight: e.target.checked }))}
              />
              Вести ночь автоматически (таймеры + озвучка)
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={settings.manualVoting ?? true}
                onChange={(e) => setSettings((s) => ({ ...s, manualVoting: e.target.checked }))}
              />
              Голосование завершает ведущий (таймер уходит в минус)
            </label>
            {settings.manualVoting && (
              <p className="hint">
                На голосовании даётся по минуте на каждого живого игрока. Когда время выходит, таймер уходит в минус и ждёт ведущего.
              </p>
            )}
            <div className="rule" />
            <label className="check">
              <input
                type="checkbox"
                disabled={!settings.includeDoctor}
                checked={settings.doctorCanSelfSave ?? true}
                onChange={(e) => setSettings((s) => ({ ...s, doctorCanSelfSave: e.target.checked }))}
              />
              Доктор может спасать себя
            </label>
          </div>
        </aside>
      </div>
    </GameLayout>
  );
}

function defaultCreateSettings(): Partial<GameSettings> {
  return {
    doctorCanSelfSave: true,
    includeDoctor: true,
    includeProstitute: false,
    startWithDay: false,
    autoNight: true,
    manualVoting: true,
  };
}

function initialSettings(routeState: unknown): Partial<GameSettings> {
  const fromRoute = readRouteSettings(routeState);
  if (fromRoute) return fromRoute;

  try {
    const raw = localStorage.getItem(LAST_MAFIA_SETTINGS_KEY);
    if (!raw) return defaultCreateSettings();
    return sanitizeSettings(JSON.parse(raw)) ?? defaultCreateSettings();
  } catch {
    return defaultCreateSettings();
  }
}

function readRouteSettings(routeState: unknown): Partial<GameSettings> | null {
  if (!routeState || typeof routeState !== 'object') return null;
  const settings = (routeState as { settings?: unknown }).settings;
  return sanitizeSettings(settings);
}

function saveLastSettings(settings: Partial<GameSettings>): void {
  try {
    localStorage.setItem(LAST_MAFIA_SETTINGS_KEY, JSON.stringify(sanitizeSettings(settings) ?? settings));
  } catch {
    // Ignore browsers where storage is unavailable.
  }
}

function sanitizeSettings(value: unknown): Partial<GameSettings> | null {
  if (!value || typeof value !== 'object') return null;
  const input = value as Partial<GameSettings>;
  const output: Partial<GameSettings> = {};

  if (typeof input.presetId === 'string') output.presetId = input.presetId;
  if (typeof input.doctorCanSelfSave === 'boolean') output.doctorCanSelfSave = input.doctorCanSelfSave;
  if (typeof input.includeDoctor === 'boolean') output.includeDoctor = input.includeDoctor;
  if (typeof input.includeProstitute === 'boolean') output.includeProstitute = input.includeProstitute;
  if (typeof input.startWithDay === 'boolean') output.startWithDay = input.startWithDay;
  if (typeof input.autoNight === 'boolean') output.autoNight = input.autoNight;
  if (typeof input.manualVoting === 'boolean') output.manualVoting = input.manualVoting;
  if (typeof input.audioEnabled === 'boolean') output.audioEnabled = input.audioEnabled;
  if (typeof input.revealRoleOnElimination === 'boolean') output.revealRoleOnElimination = input.revealRoleOnElimination;
  if (typeof input.discussionSeconds === 'number') {
    output.discussionSeconds = input.discussionSeconds === 180 ? DEFAULT_SETTINGS.discussionSeconds : input.discussionSeconds;
  }
  if (typeof input.votingSeconds === 'number') output.votingSeconds = input.votingSeconds;
  if (typeof input.nightActionSeconds === 'number') output.nightActionSeconds = input.nightActionSeconds;
  if (typeof input.roleRevealSeconds === 'number') output.roleRevealSeconds = input.roleRevealSeconds;

  return Object.keys(output).length > 0 ? { ...defaultCreateSettings(), ...output } : null;
}

function RoleLine({ label, count, accent }: { label: string; count: number; accent?: boolean }) {
  return (
    <div className="role-line">
      <span className={accent ? 'role-accent' : ''}>{label}</span>
      <span className="mono">{count}</span>
    </div>
  );
}

function countValidation(count: number): { ok: boolean; message: string } {
  if (count < 5) return { ok: false, message: `Нужно минимум 5 игроков. Сейчас ${count}.` };
  if (count > 12) return { ok: false, message: `Максимум 12 игроков. Сейчас ${count}.` };
  return { ok: true, message: '' };
}

function CreateStyles() {
  return (
    <style>{`
      .cr-title { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 64px; line-height: 1.02; letter-spacing: -.015em; margin: 12px 0 0; }
      .lede { max-width: 640px; font-size: 16px; line-height: 1.55; color: var(--ink-dim); margin: 20px 0 40px; }
      .cr-layout { display: grid; grid-template-columns: 1.5fr 1fr; gap: 24px; align-items: start; }
      .panel { background: var(--bg2); border: 1px solid var(--line); border-radius: var(--radius-card); padding: 24px; margin-bottom: 16px; }
      .panel-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 16px; }
      .panel-head h2 { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 28px; margin: 0; letter-spacing: -.005em; }
      .team-row { margin-bottom: 12px; }
      .text-input { width: 100%; background: var(--bg); border: 1px solid var(--line-strong); border-radius: var(--radius-btn); color: var(--ink); padding: 12px 14px; font-family: 'Space Grotesk', sans-serif; font-size: 15px; outline: none; }
      .text-input:focus { border-color: var(--accent); }
      select.text-input { appearance: none; cursor: pointer; }
      .players { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }
      .player-row { display: grid; grid-template-columns: 32px 1fr auto 40px; gap: 10px; align-items: center; }
      .pidx { font-size: 12px; color: var(--ink-faint); text-align: center; }
      .host-note { grid-column: 1 / -1; margin: 0 0 2px; color: var(--ink-dim); font-size: 13px; line-height: 1.45; }
      .host-check { display: inline-flex; align-items: center; gap: 6px; color: var(--ink-dim); font-size: 12px; white-space: nowrap; cursor: pointer; }
      .host-check input { width: 16px; height: 16px; margin: 0; accent-color: var(--accent); cursor: pointer; }
      .icon-btn { appearance: none; border: 1px solid var(--line); background: transparent; color: var(--ink-dim); border-radius: var(--radius-btn); font-size: 18px; cursor: pointer; height: 44px; transition: color .2s, border-color .2s; }
      .icon-btn:hover:not(:disabled) { color: var(--danger); border-color: var(--danger); }
      .icon-btn:disabled { opacity: .4; cursor: not-allowed; }
      .add-row { display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 20px; }
      .cr-actions { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
      .warn { font-size: 13px; color: var(--ink-dim); margin-top: 12px; }
      .warn-err { color: var(--danger); }
      .warn-ok { color: var(--accent); }
      .role-comp { display: flex; flex-direction: column; gap: 8px; }
      .role-line { display: flex; justify-content: space-between; align-items: baseline; font-size: 18px; }
      .role-accent { color: var(--accent); }
      .rule { height: 1px; background: var(--line); margin: 16px 0; }
      .hint { font-size: 13px; color: var(--ink-dim); line-height: 1.5; margin: 0 0 12px; }
      .check { display: flex; align-items: center; gap: 10px; font-size: 14px; color: var(--ink-dim); cursor: pointer; }
      .check input { accent-color: var(--accent); width: 18px; height: 18px; }
      .check input:disabled { cursor: not-allowed; }
      .check:has(input:disabled) { opacity: .5; }
      @media (max-width: 900px) { .cr-layout { grid-template-columns: 1fr; } .cr-title { font-size: 44px; } }
    `}</style>
  );
}
